import { StrictMode, Component } from 'react'
import type { ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import App from './App.tsx'

declare global {
  interface Window {
    __sentryCapture?: (error: unknown) => void
  }
}

// Lightweight error boundary — keeps @sentry/react (~245 KB) off the critical
// render path. Shows the fallback UI immediately and forwards the error to
// Sentry if/once it has finished loading (deferred init below).
class RootErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  componentDidCatch(error: unknown) {
    window.__sentryCapture?.(error)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center', color: '#fff' }}>
          <h2>Something went wrong</h2>
          <p style={{ color: '#888' }}>The error has been reported. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: 16, padding: '8px 16px' }}
          >
            Refresh
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RootErrorBoundary>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </RootErrorBoundary>
  </StrictMode>,
)

// Fade out the pre-hydration splash once React has committed its first frame.
// rAF fires after the first paint, so the app shell is already on screen.
// Append `?splash=hold` to hold the splash for 3s (demos / screenshots).
requestAnimationFrame(() => {
  const splash = document.getElementById('pwa-splash')
  if (!splash) return
  const hold = new URLSearchParams(window.location.search).get('splash') === 'hold'
  const delay = hold ? 3000 : 0
  setTimeout(() => {
    splash.classList.add('pwa-splash-hidden')
    setTimeout(() => splash.remove(), 300)
  }, delay)
})

// Defer ALL of Sentry off the critical path — dynamically import + init only
// after the browser is idle, so the ~245 KB SDK never blocks first paint / LCP.
// Replay (heavy) is added last, same as before.
const initSentry = async () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return
  const Sentry = await import('@sentry/react')
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^AbortError/,
      /Failed to fetch/,
    ],
  })
  window.__sentryCapture = (error: unknown) => Sentry.captureException(error)
  Sentry.addIntegration(
    Sentry.replayIntegration({ maskAllText: false, blockAllMedia: true }),
  )
}

if (typeof window.requestIdleCallback === 'function') {
  window.requestIdleCallback(() => void initSentry(), { timeout: 3000 })
} else {
  setTimeout(() => void initSentry(), 2000)
}
